import type { MouseEvent } from "react";

export function easeInOutCubic(t: number) {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

export type SmoothScrollOptions = {
  /** Pixel offset below the fixed header on narrow viewports (under 768px width). Default 64. */
  headerOffsetMobile?: number;
  /** Pixel offset on larger viewports. Default 80. */
  headerOffsetDesktop?: number;
};

/** Height of the fixed announcement bar, in pixels. Zero when it is hidden. */
export function bannerHeightPx(): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--banner-h").trim();
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) ? value : 0;
}

function headerOffsetPx(options?: SmoothScrollOptions) {
  const mobile = options?.headerOffsetMobile ?? 64;
  const desktop = options?.headerOffsetDesktop ?? 80;
  return (window.innerWidth < 768 ? mobile : desktop) + bannerHeightPx();
}

export function smoothScrollTo(elementId: string, options?: SmoothScrollOptions) {
  const el = document.getElementById(elementId);
  if (!el) return;
  const headerOffset = headerOffsetPx(options);
  const targetY = el.getBoundingClientRect().top + window.scrollY - headerOffset;
  const startY = window.scrollY;
  const diff = targetY - startY;
  if (Math.abs(diff) < 1) return;
  const duration = Math.min(800, Math.max(400, Math.abs(diff) * 0.5));
  let start: number | null = null;

  function step(timestamp: number) {
    if (start === null) start = timestamp;
    const elapsed = timestamp - start;
    const progress = Math.min(elapsed / duration, 1);
    window.scrollTo(0, startY + diff * easeInOutCubic(progress));
    if (progress < 1) requestAnimationFrame(step);
  }

  requestAnimationFrame(step);
}

const DEFAULT_MAX_ATTEMPTS = 90;

/** Drops an in-flight correction when a newer section scroll starts. */
let settleToken = 0;

/**
 * Waits until `document.getElementById(elementId)` exists (e.g. lazy below-the-fold
 * sections), then smooth-scrolls. No-ops if the element never appears.
 *
 * Sections above the target can still change height after that first scroll
 * (the resources block loads in). A short follow-up puts the heading back
 * under the header, and stops if the reader scrolls on their own.
 */
export function scrollToSectionIdWhenReady(
  elementId: string,
  options?: SmoothScrollOptions & { maxAttempts?: number },
) {
  const token = ++settleToken;
  const max = options?.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
  let attempts = 0;

  function tick() {
    if (token !== settleToken) return;
    const el = document.getElementById(elementId);
    if (el) {
      smoothScrollTo(elementId, options);
      window.setTimeout(() => settleAfterLayout(elementId, options, token), 900);
      return;
    }
    if (attempts < max) {
      attempts += 1;
      requestAnimationFrame(tick);
    }
  }

  requestAnimationFrame(tick);
}

function settleAfterLayout(elementId: string, options: SmoothScrollOptions | undefined, token: number) {
  if (token !== settleToken) return;
  let expected = window.scrollY;
  const started = performance.now();

  const correct = () => {
    if (token !== settleToken) return;
    if (Math.abs(window.scrollY - expected) > 2) return;
    const node = document.getElementById(elementId);
    if (!node) return;
    const delta = node.getBoundingClientRect().top - headerOffsetPx(options);
    if (Math.abs(delta) > 24) {
      window.scrollTo(0, window.scrollY + delta);
      expected = window.scrollY;
    }
    if (performance.now() - started < 1000) window.setTimeout(correct, 200);
  };

  correct();
}

/**
 * Use on in-page hash links: prevents the default jump and smooth-scrolls to the target id.
 */
export function handleHashLinkClick(
  e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  href: string,
  scrollOptions?: SmoothScrollOptions,
) {
  const id = href.replace("#", "");
  if (!id) return;
  e.preventDefault();
  smoothScrollTo(id, scrollOptions);
}
